import { IPublishDestination, IPublishVenue } from '../../types';

export interface IVenueWithChannels {
  venue: IPublishVenue;
  channelNames: string[];
}

/**
 * Builds a de-duplicated, alphabetically sorted list of venues across the selected channels,
 * tracking which of those channels each venue publishes to.
 *
 * When `venueSelection` is provided, only venues explicitly selected there are included — this is
 * the single source of truth for the effective channel∩venue intersection used by the venue list,
 * the summary and the publish payload.
 */
export const getVenuesWithChannels = (
  publicationData: IPublishDestination[],
  selectedChannelIds: string[] = [],
  venueSelection?: string[],
): IVenueWithChannels[] => {
  const selectedChannelSet = new Set(selectedChannelIds);
  const venueSelectionSet = venueSelection ? new Set(venueSelection) : null;
  const venueMap = new Map<string, IVenueWithChannels>();

  publicationData
    .filter((channel) => selectedChannelSet.has(channel.channel_id))
    .forEach((channel) => {
      channel.venues.forEach((venue) => {
        if (venueSelectionSet && !venueSelectionSet.has(venue.venue_id)) return;

        const existing = venueMap.get(venue.venue_id);
        if (existing) {
          existing.channelNames.push(channel.name);
        } else {
          venueMap.set(venue.venue_id, { venue, channelNames: [channel.name] });
        }
      });
    });

  return Array.from(venueMap.values()).sort((a, b) => a.venue.name.localeCompare(b.venue.name));
};
